import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { ScrollArea } from './scroll-area'

/**
 * Card — контейнер-поверхность (`.card` макета). По умолчанию без тени (только граница из токенов);
 * тень — лишь в варианте `elevated` (приподнятая поверхность). Опциональные области заголовка/подвала.
 *
 * `scrollable` — карточка с прокручиваемым телом: заголовок и подвал остаются на месте, а длинное
 * содержимое прокручивается внутри карточки аккуратной полосой `ScrollArea`. Высоту ограничивает
 * `className` (например, `max-h-dialog` у карточки в модальном окне).
 */
const cardVariants = cva('ds-surface rounded-12', {
  variants: {
    variant: {
      surface: 'shadow-none',
      elevated: 'shadow-elevated',
    },
  },
  defaultVariants: { variant: 'surface' },
})

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {
  header?: ReactNode
  /** Доп. классы контейнера заголовка (напр. `sticky top-0` для липкой шапки в скролл-модалке). */
  headerClassName?: string
  footer?: ReactNode
  /** Прокручиваемое тело при ограниченной высоте карточки (заголовок и подвал не прокручиваются). */
  scrollable?: boolean
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    { className, variant, header, headerClassName, footer, scrollable = false, children, ...props },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant }), scrollable && 'flex flex-col', className)}
      {...props}
    >
      {header ? (
        <div
          className={cn(
            'border-b border-line px-5 py-4 font-semibold',
            scrollable && 'shrink-0',
            headerClassName,
          )}
        >
          {header}
        </div>
      ) : null}
      {scrollable ? (
        <ScrollArea className="min-h-0 flex-1" viewportClassName="px-5 py-4">
          {children}
        </ScrollArea>
      ) : (
        <div className="px-5 py-4">{children}</div>
      )}
      {footer ? (
        <div className={cn('border-t border-line px-5 py-4', scrollable && 'shrink-0')}>
          {footer}
        </div>
      ) : null}
    </div>
  ),
)
Card.displayName = 'Card'

export { cardVariants }
